import React, { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { saveAdminReturnTo } from '../lib/returnTo';

/**
 * ProtectedAdminRoute — gates every /admin/* URL.
 *
 * Rules (Feb 2026 admin portal redesign):
 *   • Loading state → spinner (never flash-through).
 *   • Not signed in → save the requested URL, redirect to /admin (login).
 *   • Signed-in non-admin (student, coach) → redirect to /admin (login page
 *     shows "Not authorised" strip after they attempt sign-in there).
 *     NEVER punt them to the removed Student Portal or the public /login.
 *   • Signed-in admin with must_change_password === true → redirect to
 *     /admin/change-password unless already on that route.
 *   • Optional `requiredPermission` prop can gate a specific action
 *     ("payment:approve_normal"). If the caller lacks it we render the
 *     shared /admin/access-denied page instead of a raw 403.
 */
const hasPerm = (user, action, permsList) => {
  if (!user || user.role !== 'admin') return false;
  if (!action) return true;
  if (Array.isArray(permsList) && permsList.length) return permsList.includes(action);
  // Legacy admin (no sub-role) → treat as super for backwards compat
  const sub = user.admin_permissions || 'none';
  if (sub === 'none' || sub === 'super_admin') return true;
  // We only need the permissions list from /admin/me/permissions but the
  // list is not on user object here — the AuthProvider does not fetch it.
  // Fall back to "unknown → deny" only if permsList is provided; otherwise
  // let the backend be authoritative.
  return true;
};

export const ProtectedAdminRoute = ({ children, requiredPermission }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  useEffect(() => {
    if (!loading && !user) {
      saveAdminReturnTo(`${location.pathname}${location.search}${location.hash}`);
    }
  }, [loading, user, location]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen" data-testid="admin-loading">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#7E22CE] border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-sm text-slate-500">Loading admin portal…</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/admin" replace />;
  }

  if (user.role !== 'admin') {
    return <Navigate to="/admin" replace />;
  }

  const onChangePwdRoute = location.pathname === '/admin/change-password';
  if (user.must_change_password && !onChangePwdRoute) {
    return <Navigate to="/admin/change-password" replace />;
  }

  if (requiredPermission && !hasPerm(user, requiredPermission)) {
    return <Navigate to="/admin/access-denied" replace />;
  }

  return children;
};

export default ProtectedAdminRoute;
